import { useEffect, useState } from 'react'
import { parseStat } from './dashboardTheme.js'

/**
 * StatCard — one `stats[]` figure from a product's screen.
 *
 * The figure is read, not just printed: "2 of 6" is drawn as a ring that is a third full, "78%" as
 * a gauge under the number, and a plain number counts up once when the card appears. Anything it
 * cannot read as one of those is printed as the product sent it. The words printed are always the
 * product's own, so a picture is only ever an addition to the number, never a replacement.
 *
 * `colour` is this card's accent; the cards of a screen take the theme's colours in turn.
 */
const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** 0 → target over about a second, easing out. Under reduced motion, or for a zero, it is simply
 *  the target. */
function useCountUp(target, on) {
  const [v, setV] = useState(on && !reduced() ? 0 : target)
  useEffect(() => {
    if (!on || reduced() || !target) { setV(target); return undefined }
    let raf, t0
    const step = (t) => {
      t0 = t0 ?? t
      const k = Math.min(1, (t - t0) / 900)
      setV(target * (1 - Math.pow(1 - k, 3)))
      if (k < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, on])
  return v
}

function Ring({ fraction, colour, size = 64 }) {
  const r = (size - 10) / 2, c = 2 * Math.PI * r
  const [f, setF] = useState(reduced() ? fraction : 0)
  useEffect(() => { const id = requestAnimationFrame(() => setF(fraction)); return () => cancelAnimationFrame(id) }, [fraction])
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 -rotate-90" aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eceae6" strokeWidth="8" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colour} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={c * (1 - f)} style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.2,.8,.2,1)' }} />
    </svg>
  )
}

const group = (n, decimals, commas) => (commas ? n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : n.toFixed(decimals))

export default function StatCard({ stat, colour }) {
  const p = parseStat(stat?.value)
  const counted = useCountUp(p.kind === 'number' ? p.n : 0, p.kind === 'number')
  const shown = p.kind === 'empty' ? '—'
    : p.kind === 'number' ? `${p.prefix}${group(counted, p.decimals, p.commas)}${p.suffix}`
    : String(stat.value)

  return (
    <div className="pt-rise relative overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-xs hover:shadow-md transition-shadow">
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: colour }} aria-hidden="true" />
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 break-words">{shown}</p>
          <p className="text-xs font-semibold text-gray-700 mt-1">{stat?.label}</p>
          {stat?.hint && <p className="text-[11px] leading-snug text-gray-500 mt-0.5">{stat.hint}</p>}
        </div>
        {p.kind === 'ratio' && (
          <div className="relative shrink-0 hidden sm:block" title={`${p.a} of ${p.b}`}>
            <Ring fraction={p.a / p.b} colour={colour} />
            <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-gray-700">
              {Math.round((p.a / p.b) * 100)}%
            </span>
          </div>
        )}
      </div>
      {p.kind === 'percent' && (
        <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
          <div className="h-full rounded-full pt-grow" style={{ width: `${p.n}%`, background: colour }} />
        </div>
      )}
    </div>
  )
}
